import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDealersByCountry, getDealersByVicinityId } from '@yme/api';
import { CrossedMapBallon, MapBalloon } from '@yme/assets/icons';
import { AlertType } from '@yme/atoms/alert';
import { LEADING_ICON, TEXT_VARIANT } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { Col, Row } from '@yme/atoms/grid';
import { Pagination } from '@yme/atoms/pagination';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { useWindowSize } from '@yme/common/hooks';
import { removeDistanceFromDealers } from '@yme/components/DealerLocatorPage/helpers';
import { sortDealersByDistance } from '@yme/components/LeadForm/FormContent/helpers';
import { SectionWrapper } from '@yme/molecules/section-wrapper';
import { getCurrentCountryRoot, getInitialCountryRoot, getIsoCountryCode, } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { DigitalDataMutation, buildKey, buildStateParam, getAdditionalCounties, setDigitalData, } from '@yme/utils/common';
import { GB_COUNTRY_ROOT } from '@yme/utils/constants';
import { addDistanceToDealers } from '@yme/utils/dealer';
import { mimicDelay } from '@yme/utils/mimicDelay';
import { PreferredDealer } from '../preferred-dealer';
import { TestRideContent } from './TestRideContent';
import { DRIVER_LICENCE_CONFIG, HEADER_MARGIN, LICENSE_DROPDOWN_LABEL, TEST_RIDE_WRAPPER_ID, } from './constants';
import { AlertStyled, AlertWrapper, DropdownMobileWrapperStyled, FilterByDealerButton, PreferredDealerWrapper, ShowAllDealersButton, TestRideGridWrapperStyled, } from './styled';
import { useTestRideGrid } from './useTestRideGrid';
import { useTestRideIframeError } from './useTestRideIframeError';
export function TestRideGrid({ productData, testRideFormLinks, isLoading, isLoaded, isError, isRightToLeft, isShowDriverLicenseFilter, languageRootPath, isInIframeError, isInEditor, dealerIdList, googleApiKey, }) {
    const [dealerName, setDealerName] = useState('');
    const [dealers, setDealers] = useState([]);
    const [isDealerFieldVisible, setIsDealerFieldVisible] = useState(false);
    const [isFilterByDealerButtonVisible, setIsFilterByDealerButtonVisible] = useState(true);
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [isClearPreferredDealer, setIsClearPreferredDealer] = useState(false);
    const [selectedDealerId, setSelectedDealerId] = useState(null);
    const { t, language: currentLanguage } = useTranslation();
    const { isMobileView, isInfiniteDesktopView } = useWindowSize();
    const history = useHistory();
    const isDealerQueryParamsLoaded = useRef(false);
    const isDealersLoaded = useRef(false);
    const currentCountry = useSelector(getCurrentCountryRoot);
    const isoCountryCode = useSelector(getIsoCountryCode);
    const initialCountryRoot = useSelector(getInitialCountryRoot);
    const isGBCountyRoot = initialCountryRoot === GB_COUNTRY_ROOT;
    useEffect(() => {
        if (dealerIdList.length) {
            getDealersByVicinityId(dealerIdList).then((data) => {
                const dealerNames = data.map((dealer) => dealer.name);
                setDealerName(dealerNames.join(', '));
                setSelectedDealerId(data[0].vicinityId);
                setIsAlertVisible(true);
            });
        }
    }, [dealerIdList]);
    const { config, demoUnits, paginationConfigData, paginationConfig, activeItemValue, onClickItemHandler, setCurrentPerPageValue, filterOnChangeHandler, } = useTestRideGrid({
        isShowDriverLicenseFilter,
        isMobileView,
        productData,
    });
    useTestRideIframeError({
        isInIframeError,
        languageRootPath,
    });
    useEffect(() => {
        if (dealers.length && !isDealerQueryParamsLoaded.current) {
            if (dealerIdList.length) {
                const filteredDealerIds = dealerIdList.reduce((acc, id) => {
                    if (dealers.some((dealer) => dealer.vicinityId === id)) {
                        return [...acc, id];
                    }
                    return acc;
                }, []);
                if (filteredDealerIds.length === dealerIdList.length) {
                    return;
                }
                const searchParams = new URLSearchParams(location.search);
                if (filteredDealerIds.length) {
                    searchParams.set('dealerId', filteredDealerIds.join(','));
                }
                else {
                    ['dealerIds', 'dealerId'].forEach((param) => {
                        searchParams.delete(param);
                    });
                }
                history.push({
                    search: searchParams.toString(),
                });
                isDealerQueryParamsLoaded.current = true;
            }
            else {
                isDealerQueryParamsLoaded.current = true;
            }
        }
    }, [dealerIdList, dealers, history]);
    const onItemClicked = useCallback((sku, url) => {
        setDigitalData(DigitalDataMutation.TEST_RIDE_SELECT_PRODUCT, {
            sku,
            url,
        });
    }, []);
    const LicenseDropdown = useMemo(() => (React.createElement(DropdownMobileWrapperStyled, null,
        React.createElement(Dropdown, { id: buildKey(LICENSE_DROPDOWN_LABEL, buildStateParam()), activeItem: config.driverLicense, items: DRIVER_LICENCE_CONFIG, isRightToLeft: isRightToLeft, contents: true, onChange: filterOnChangeHandler, isShowIconThenListOpened: true, useForMobile: true }))), [config.driverLicense, filterOnChangeHandler, isRightToLeft]);
    const clearDealersIdParams = useCallback(() => {
        const params = new URLSearchParams(history.location.search);
        ['dealerIds', 'dealerId'].forEach((param) => {
            params.delete(param);
        });
        history.replace({
            pathname: location.pathname,
            search: params.toString(),
            hash: location.hash,
        });
    }, [history]);
    const handleShowAllDialers = useCallback(async () => {
        clearDealersIdParams();
        setSelectedDealerId(null);
        setIsClearPreferredDealer(true);
        await mimicDelay();
        setIsClearPreferredDealer(false);
    }, [clearDealersIdParams]);
    const handleChangeDialer = useCallback(() => {
        setIsFilterByDealerButtonVisible(true);
        setIsDealerFieldVisible(true);
        setIsAlertVisible(false);
    }, []);
    const handleSetSelectedDealerId = useCallback((selectedDealerId) => {
        setSelectedDealerId(selectedDealerId);
    }, []);
    const loadFilteredNearestDealers = useCallback(async () => {
        try {
            const dealers = await getDealersByCountry(currentCountry, currentLanguage);
            return dealers;
        }
        catch {
            return [];
        }
    }, [currentCountry, currentLanguage]);
    const loadDealers = useCallback(async (location) => {
        let dealersData = [];
        if (dealers.length) {
            dealersData = Array.from(dealers);
        }
        else {
            dealersData = await loadFilteredNearestDealers();
        }
        const dealersResult = location
            ? sortDealersByDistance(addDistanceToDealers(dealersData, location.coordinates))
            : removeDistanceFromDealers(dealersData);
        setDealers(dealersResult);
        if (location && dealersResult.length) {
            handleSetSelectedDealerId(dealersResult[0]?.vicinityId);
        }
    }, [loadFilteredNearestDealers, handleSetSelectedDealerId, dealers]);
    useEffect(() => {
        if (!isDealersLoaded.current) {
            loadDealers().finally(() => {
                isDealersLoaded.current = true;
            });
        }
    }, [loadDealers]);
    const onApplyDealerSelection = useCallback(() => {
        if (selectedDealerId) {
            const params = new URLSearchParams();
            params.set('dealerId', selectedDealerId);
            history.push({
                search: params.toString(),
            });
            setIsDealerFieldVisible(false);
            setIsAlertVisible(true);
        }
    }, [history, selectedDealerId]);
    const handleLocationSelect = useCallback((location) => {
        if (!location) {
            return;
        }
        loadDealers(location);
    }, [loadDealers]);
    const ShowAllDialersAlert = useMemo(() => (React.createElement(AlertStyled, { isInfiniteDesktopView: isInfiniteDesktopView, isFullWidth: true, message: t('productsGrid_selectedDealer_label') + dealerName, type: AlertType.INFO },
        React.createElement(FlexWrapper, { alignItems: "center", gap: "16px", isFullWidth: true },
            React.createElement(ShowAllDealersButton, { onClick: handleShowAllDialers }, t('productsGrid_showAllDealersVehicles_label')),
            React.createElement(ShowAllDealersButton, { onClick: handleChangeDialer }, t('productsGrid_changeDealer_label'))))), [dealerName, handleChangeDialer, handleShowAllDialers, isInfiniteDesktopView, t]);
    const isShowAllDialersAlertVisible = useMemo(() => {
        return Boolean(dealerIdList.length && isAlertVisible);
    }, [dealerIdList.length, isAlertVisible]);
    useEffect(() => {
        setIsFilterByDealerButtonVisible(!isShowAllDialersAlertVisible);
    }, [isShowAllDialersAlertVisible]);
    const handleFilterByDealerButton = useCallback(() => {
        if (isDealerFieldVisible && dealerIdList.length) {
            setIsAlertVisible(true);
        }
        setIsDealerFieldVisible((prev) => !prev);
    }, [dealerIdList.length, isDealerFieldVisible]);
    const rightSideContent = useMemo(() => {
        return (React.createElement(FlexWrapper, { isFullWidth: isMobileView, alignItems: "center", gap: "16px", flexWrap: isMobileView ? 'wrap' : undefined },
            isFilterByDealerButtonVisible && (React.createElement(FilterByDealerButton, { label: isDealerFieldVisible
                    ? t('productsGrid_hideFilterByDealer_button')
                    : t('productsGrid_filterByDealerAvailability_button'), buttonVariant: TEXT_VARIANT, hasIconButton: LEADING_ICON, iconComponent: isDealerFieldVisible ? CrossedMapBallon : MapBalloon, onClick: handleFilterByDealerButton })),
            isShowDriverLicenseFilter ? LicenseDropdown : undefined));
    }, [
        isMobileView,
        isFilterByDealerButtonVisible,
        isDealerFieldVisible,
        t,
        handleFilterByDealerButton,
        isShowDriverLicenseFilter,
        LicenseDropdown,
    ]);
    return (React.createElement(TestRideGridWrapperStyled, { id: TEST_RIDE_WRAPPER_ID },
        React.createElement(SectionWrapper, { headerLeftSide: t('productsGrid_chooseModel_header'), isRightToLeft: isRightToLeft, width: "100%", maxWidth: "1440px", isVerticalIndentDefault: false, isExpanded: false, blockPosition: "relative", desktopRatio: {
                leftSide: 4,
                rightSide: 8,
            }, headerRightSide: rightSideContent },
            isShowAllDialersAlertVisible && React.createElement(AlertWrapper, null, ShowAllDialersAlert),
            React.createElement(Row, null,
                React.createElement(Col, null,
                    React.createElement(PreferredDealerWrapper, { isDealerFieldVisible: isDealerFieldVisible, isFullWidth: true, flexDirection: "column" },
                        React.createElement(PreferredDealer, { isGBCountyRoot: isGBCountyRoot, dealers: dealers, additionalCountries: getAdditionalCounties(isoCountryCode), isClearPreferredDealer: isClearPreferredDealer, country: isoCountryCode, isRightToLeft: isRightToLeft, googleApiKey: googleApiKey, inputTitleLabel: t('productsGrid_locationInput_label'), selectedDealerId: selectedDealerId, onApplySelection: onApplyDealerSelection, onDealerSelect: handleSetSelectedDealerId, onLocationSelect: handleLocationSelect, isShowChooseFromMap: true })))),
            React.createElement(TestRideContent, { isRightToLeft: isRightToLeft, isError: isError, isInEditor: isInEditor, onClick: onItemClicked, isLoaded: isLoaded, demoUnits: demoUnits, testRideFormLinks: testRideFormLinks, languageRootPath: languageRootPath, currentPerPageValue: Number(paginationConfigData.activeElement) }),
            React.createElement(Row, { mt: "24px" },
                React.createElement(Col, null, isLoaded && Boolean(config) && (React.createElement(Pagination
                // common props
                , { 
                    // common props
                    isRightToLeft: isRightToLeft, isMobileView: isMobileView, 
                    // Pagination props
                    paginationData: paginationConfigData, paginationConfig: paginationConfig, activeItemValue: activeItemValue, onClickItemHandler: onClickItemHandler, 
                    // PerPage props
                    perPageLabel: t('filters_itemsPerPage_label'), currentPerPageValue: config.perPage, setCurrentPerPageValue: setCurrentPerPageValue, scrollToElement: {
                        id: TEST_RIDE_WRAPPER_ID,
                        yOffset: HEADER_MARGIN,
                    } })))),
            React.createElement(SpinnerLoader, { isLoading: isLoading }))));
}
TestRideGrid.displayName = 'TestRideGrid';
